import type { ParticipantBehaviour, ParticipantWidgetLayoutItem } from '@piccolohealth/pbs-common';
import { ChakraV3, Empty, Spin, useDebouncedCallback } from '@piccolohealth/ui';
import { DateTime, P } from '@piccolohealth/util';
import React from 'react';
import { BiSolidBarChartAlt2 } from 'react-icons/bi';
import { PiccoloError } from '../../../components/generic/PiccoloError';
import {
  useParticipantBehavioursRecordingsQuery,
  useParticipantWidgetLayoutItemsQuery,
} from '../../../graphql/hooks/useParticipantQuery';
import { useUpdateParticipantWidgetLayoutItemsMutation } from '../../../graphql/hooks/useUpdateParticipantWidgetLayoutItemsMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { type UseGridReturn, useGrid } from '../hooks/useGrid';
import { ParticipantDataGridItem } from './grid/ParticipantDataGridItem';
import { ParticipantWidgetLayoutItemAddControl } from './grid/ParticipantWidgetLayoutItem';
import { ParticipantWidgetTimeRangeControl } from './ParticipantWidgetTimeRangeControl';

import 'gridstack/dist/gridstack-extra.min.css';
import 'gridstack/dist/gridstack.min.css';
import { ParticipantBehaviourRecordingCreateButton } from '../../behaviours/components/ParticipantBehaviourRecordingCreateButton';
import { ParticipantWidgetDataZoom } from './timeseries/ParticipantWidgetDataZoom';

interface ParticipantDataContentProps {
  participantId: string;
  participantBehaviours: ParticipantBehaviour[];
  items: ParticipantWidgetLayoutItem[];
  grid: UseGridReturn<ParticipantWidgetLayoutItem>;
  isDisabled?: boolean;
}

const ParticipantWidgetContent = (props: ParticipantDataContentProps) => {
  const { participantId, participantBehaviours, items, grid, isDisabled } = props;

  const { gridRef, refs } = grid;

  const content = P.run(() => {
    if (P.isEmpty(items)) {
      return (
        <Empty
          title='No data widgets'
          description='Add a data widget to display data for this participant'
          extra={
            <ParticipantWidgetLayoutItemAddControl
              participantId={participantId}
              onAdd={grid.onAdd}
              isDisabled={isDisabled}
            />
          }
          icon={
            <ChakraV3.Icon fontSize='48' color='gray.300'>
              <BiSolidBarChartAlt2 />
            </ChakraV3.Icon>
          }
        />
      );
    }

    return (
      <>
        {items.map((item) => {
          return (
            <ChakraV3.Stack
              key={item.id}
              ref={refs.current[item.id]}
              className='grid-stack-item'
              h='full'
              w='full'
            >
              <ChakraV3.Stack
                className='grid-stack-item-content'
                bg='white'
                layerStyle='outline.subtle'
                rounded='xl'
              >
                <ParticipantDataGridItem
                  participantId={participantId}
                  item={item}
                  grid={grid}
                  participantBehaviours={participantBehaviours}
                  isDisabled={isDisabled}
                />
              </ChakraV3.Stack>
            </ChakraV3.Stack>
          );
        })}
      </>
    );
  });

  return (
    <ChakraV3.Stack h='full' w='full'>
      <ChakraV3.Stack
        ref={gridRef}
        className='grid-stack'
        display='block'
        overflow='hidden'
        height='100%'
        minHeight='100% !important'
      >
        {content}
      </ChakraV3.Stack>
    </ChakraV3.Stack>
  );
};

interface Props {
  participantId: string;
  tabButtons: React.ReactNode;
  isDisabled?: boolean;
}

export const ParticipantDataWidgetsTab = (props: Props) => {
  const { participantId, tabButtons, isDisabled } = props;

  const { organization, errorToast } = useAppContext();

  const [startDate, setStartDate] = React.useState(DateTime.now().minus({ days: 30 }));
  const [endDate, setEndDate] = React.useState(DateTime.now());

  const onStartDateChange = (date: DateTime) => {
    setStartDate(date.startOf('day'));
  };

  const onEndDateChange = (date: DateTime) => {
    setEndDate(date.endOf('day'));
  };

  const updateParticipantWidgetLayoutItemsMutation =
    useUpdateParticipantWidgetLayoutItemsMutation();

  const participantWidgetLayoutItemsQuery = useParticipantWidgetLayoutItemsQuery({
    organizationId: organization.id,
    participantId,
  });

  const participantBehaviourQuery = useParticipantBehavioursRecordingsQuery({
    organizationId: organization.id,
    participantId,
  });

  const isLoading =
    participantWidgetLayoutItemsQuery.isLoading || participantBehaviourQuery.isLoading;
  const error = participantWidgetLayoutItemsQuery.error ?? participantBehaviourQuery.error;

  // Use the mutation vars, if available, otherwise use the data from the query
  const items: ParticipantWidgetLayoutItem[] =
    (updateParticipantWidgetLayoutItemsMutation.variables
      ?.request as ParticipantWidgetLayoutItem[]) ??
    ((participantWidgetLayoutItemsQuery.data?.organization?.participant?.widgets ??
      []) as ParticipantWidgetLayoutItem[]);

  const participantBehaviours =
    (participantBehaviourQuery.data?.organization?.participant
      ?.behaviours as ParticipantBehaviour[]) ?? [];

  const onUpdate = React.useCallback(
    (items: ParticipantWidgetLayoutItem[]) => {
      updateParticipantWidgetLayoutItemsMutation
        .mutateAsync({
          organizationId: organization.id,
          participantId,
          request: items,
        })
        .catch((err) => {
          errorToast(`Error updating widgets: ${err.message}`);
          throw err;
        });
    },
    [errorToast, organization.id, participantId, updateParticipantWidgetLayoutItemsMutation],
  );

  const grid = useGrid<ParticipantWidgetLayoutItem>({
    items,
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    onUpdate,
  });

  const css = {
    '& .grid-stack .grid-stack-placeholder > .placeholder-content': {
      rounded: 'xl',
    },
    '& .ui-resizable-handle': {
      bottom: '16px !important',
      right: '16px !important',
    },
  } as ChakraV3.SystemStyleObject;

  if (isLoading) {
    return <Spin />;
  }

  if (error) {
    return <PiccoloError h='full' error={error} />;
  }

  return (
    <ChakraV3.Stack w='full' h='full' gap={2} overflowY='hidden'>
      <ChakraV3.HStack w='full' px={6} pt={4}>
        {tabButtons}
        <ChakraV3.Stack flexShrink={1}>
          <ParticipantWidgetTimeRangeControl
            startDate={grid.startDate}
            endDate={grid.endDate}
            onStartDateChange={grid.onStartDateChange}
            onEndDateChange={grid.onEndDateChange}
          />
        </ChakraV3.Stack>
        <ChakraV3.Spacer />
        <ChakraV3.Stack w='full' pb={1}>
          <ParticipantWidgetDataZoom
            participantId={participantId}
            items={items}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </ChakraV3.Stack>
        <ChakraV3.HStack flexShrink={1}>
          <ParticipantWidgetLayoutItemAddControl
            participantId={participantId}
            onAdd={grid.onAdd}
            isDisabled={isDisabled}
          />
          <ParticipantBehaviourRecordingCreateButton
            participantId={participantId}
            participantBehaviours={participantBehaviours}
            colorPalette='brand'
            disabled={isDisabled}
          >
            Add recording
          </ParticipantBehaviourRecordingCreateButton>
        </ChakraV3.HStack>
      </ChakraV3.HStack>
      <ChakraV3.ScrollArea.Root w='full' h='full' bg='white' overflowY='auto' px={4} css={css}>
        <ChakraV3.ScrollArea.Viewport>
          <ChakraV3.ScrollArea.Content>
            <ParticipantWidgetContent
              participantId={participantId}
              participantBehaviours={participantBehaviours}
              items={items}
              grid={grid}
              isDisabled={isDisabled}
            />
          </ChakraV3.ScrollArea.Content>
        </ChakraV3.ScrollArea.Viewport>
        <ChakraV3.ScrollArea.Scrollbar>
          <ChakraV3.ScrollArea.Thumb />
        </ChakraV3.ScrollArea.Scrollbar>
        <ChakraV3.ScrollArea.Corner />
      </ChakraV3.ScrollArea.Root>
    </ChakraV3.Stack>
  );
};
